import type { ReactNode } from "react";
import { HINTS, TILE_ICONS, TILE_URGENTE } from "../lib/ui-constantes";
import type { Delta } from "../lib/format";

export function Icono({ id, clase = "ic" }: { id?: string; clase?: string }) {
  if (!id) return null;
  return (
    <svg className={clase} aria-hidden="true">
      <use href={`#${id}`} />
    </svg>
  );
}

/** Globo de ayuda. El CSS lo pinta con ::after desde data-tip y solo lo
 *  muestra en hover/focus — por eso el texto NO va como hijo del span. */
export function Hint({ texto, izq = false }: { texto: string; izq?: boolean }) {
  return (
    <span className={"hint" + (izq ? " izq" : "")} data-tip={texto} tabIndex={0}>
      ?
    </span>
  );
}

/** Aviso de error VISIBLE. La app actual solo hace console.error y deja
 *  datos viejos en pantalla; esto es lo que evita que alguien tome una
 *  decisión con cifras que no se actualizaron. */
export function AvisoError({
  error,
  onReintentar,
}: {
  error: Error;
  onReintentar?: () => void;
}) {
  return (
    <div className="aviso-error" role="alert">
      <div className="aviso-error-txt">
        <b>No se pudieron cargar los datos.</b>
        <span>{error.message}</span>
      </div>
      {onReintentar && (
        <button type="button" className="aviso-error-btn" onClick={onReintentar}>
          Reintentar
        </button>
      )}
    </div>
  );
}

export function Vacio({ children = "Sin datos en el periodo" }: { children?: ReactNode }) {
  return <div className="vacio">{children}</div>;
}

/**
 * Tarjetas de carga, iguales a esqueletoTiles() del original (app.js 999-1008).
 * La clase de las barras es `skel` — es la que trae el degradado y el brillo
 * animado en el CSS. (Antes usaba clases inventadas `sk`/`sk-lbl`, que no
 * existen en la hoja de estilos: salían recuadros grises vacíos.)
 * La clase extra `esqueleto` no pinta nada; sirve para que el telón y el
 * comparador sepan que la vista todavía está cargando.
 */
export function EsqueletoTiles({ n = 5 }: { n?: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="tile esqueleto" aria-hidden="true">
          <div
            className="skel"
            style={{ width: 32, height: 32, borderRadius: 9, marginBottom: 9 }}
          />
          <div className="skel" style={{ width: "70%", height: 11, marginBottom: 8 }} />
          <div className="skel" style={{ width: "50%", height: 24 }} />
        </div>
      ))}
    </>
  );
}

export function Tile({
  label,
  valor,
  sub,
  onClick,
  delta,
}: {
  label: string;
  valor: ReactNode;
  sub?: ReactNode;
  onClick?: () => void;
  delta?: Delta | null;
}) {
  const urgente = TILE_URGENTE.has(label);
  const icono = TILE_ICONS[label];
  const clase =
    "tile" + (onClick ? " link" : "") + (urgente ? " urgente" : "");
  const mostrarDelta = delta && delta.pct != null && isFinite(delta.pct);

  return (
    <div
      className={clase}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {icono && (
        <div className="ico-chip">
          <Icono id={icono} />
        </div>
      )}
      <div className="lbl">
        {label}
        {HINTS[label] && <Hint texto={HINTS[label]} />}
      </div>
      <div className="val">{valor}</div>
      <div className="sub">{sub}</div>
      {mostrarDelta && (
        <div
          className={
            "delta " +
            (delta.mejor == null ? "flat" : delta.mejor ? "up" : "down")
          }
        >
          <Icono id={delta.pct >= 0 ? "i-arrow-up" : "i-arrow-down"} />
          <span>{Math.abs(delta.pct).toFixed(1)}% vs. período anterior</span>
        </div>
      )}
    </div>
  );
}

export function Kpi({
  label,
  valor,
  clase,
  onClick,
}: {
  label: string;
  valor: ReactNode;
  clase?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={"kpi" + (onClick ? " link" : "")}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="k-lbl">
        {label}
        {HINTS[label] && <Hint texto={HINTS[label]} />}
      </div>
      <div className={"k-val " + (clase ?? "")}>{valor}</div>
      {onClick && <div className="k-ver">ver detalle →</div>}
    </div>
  );
}
